import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Tabs,
  Tab,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  Tooltip,
  Grid,
  LinearProgress,
  Skeleton,
  Snackbar,
  Alert,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  alpha,
  Button,
  Card,
  Fade,
} from "@mui/material";
import { useTheme, styled, keyframes } from "@mui/material/styles";

import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import AddTaskRoundedIcon from "@mui/icons-material/AddTaskRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";

import { Doughnut, Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from "chart.js";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import axios from "axios";
import { baseUrl } from "../../constants";
import { useNavigate } from "react-router-dom";
import { getUserInfoFromToken } from "../../utils/Main";
 
dayjs.extend(relativeTime);

// Styled Components for Advanced Design
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const GlassCard = styled(Card)(({ theme }) => ({
  background:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.background.paper, 0.85)
      : alpha("#ffffff", 0.95),
  backdropFilter: "saturate(180%) blur(20px)",
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  borderRadius: 20,
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 8px 32px rgba(0, 0, 0, 0.4)"
      : "0 8px 32px rgba(0, 0, 0, 0.08)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  animation: `${fadeInUp} 0.6s ease-out`,
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 16px 48px rgba(0, 0, 0, 0.5)"
        : "0 16px 48px rgba(0, 0, 0, 0.12)",
  },
}));

const KPICard = styled(Paper)(({ theme }) => ({
  position: "relative",
  overflow: "hidden",
  borderRadius: 20,
  padding: theme.spacing(2.5),
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  background:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.background.paper, 0.9)
      : alpha("#ffffff", 0.95),
  backdropFilter: "saturate(180%) blur(20px)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  cursor: "pointer",
  animation: `${fadeInUp} 0.6s ease-out`,
  "&:hover": {
    transform: "translateY(-6px) scale(1.02)",
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 20px 60px rgba(0, 0, 0, 0.5)"
        : "0 20px 60px rgba(0, 0, 0, 0.15)",
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.8)}, ${alpha(theme.palette.secondary.main, 0.8)})`,
    borderRadius: "20px 20px 0 0",
  },
}));

const IconContainer = styled(Box)(({ theme }) => ({
  width: 56,
  height: 56,
  borderRadius: 16,
  display: "grid",
  placeItems: "center",
  position: "relative",
  transition: "all 0.3s ease",
  "&::after": {
    content: '""',
    position: "absolute",
    inset: 0,
    borderRadius: 16,
    padding: 2,
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.3)}, ${alpha(theme.palette.secondary.main, 0.3)})`,
    WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
    WebkitMaskComposite: "xor",
    maskComposite: "exclude",
    opacity: 0,
    transition: "opacity 0.3s ease",
  },
  "&:hover::after": {
    opacity: 1,
  },
}));

const SectionCard = styled(GlassCard)(({ theme }) => ({
  padding: theme.spacing(3),
  minHeight: "100%",
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 700,
  fontSize: "0.938rem",
  minHeight: 48,
  borderRadius: 12,
  marginRight: theme.spacing(1),
  transition: "all 0.3s ease",
  "&.Mui-selected": {
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.secondary.main, 0.15)})`,
    color: theme.palette.primary.main,
  },
  "&:hover": {
    background: alpha(theme.palette.primary.main, 0.08),
  },
}));

const ProjectCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 16,
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  background:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.background.paper, 0.6)
      : alpha("#ffffff", 0.7),
  backdropFilter: "blur(10px)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  cursor: "pointer",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: 4,
    background: `linear-gradient(180deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    transform: "scaleY(0)",
    transition: "transform 0.3s ease",
  },
  "&:hover": {
    transform: "translateX(4px) translateY(-2px)",
    borderColor: alpha(theme.palette.primary.main, 0.4),
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 8px 24px rgba(0, 0, 0, 0.4)"
        : "0 8px 24px rgba(0, 0, 0, 0.1)",
    "&::before": {
      transform: "scaleY(1)",
    },
  },
}));

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

type RangeKey = "weekly" | "monthly" | "yearly";
type CompletionPoint = { date: string; count: number };
type CompletionStats = Record<string, CompletionPoint[]>;

type ProjectAnalytics = {
  id: number;
  name: string;
  tasks?: {
    todo: number;
    inProgress: number;
    done: number;
    postponed: number;
    overdue?: number;
    urgent?: number;
    reminders?: number;
  };
};

type Activity = { taskTitle: string; newStatus: string; date: string };

const numberFmt = (n: number) =>
  Intl.NumberFormat(undefined, { notation: "compact" }).format(n);

const getProgressColor = (value: number) => {
  if (value < 40) return "error";
  if (value < 70) return "warning";
  return "success";
};

// helper to make image URLs absolute (same idea as before)
const normalizeImageUrl = (url?: string) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `${baseUrl.replace(/\/+$/, "")}/${String(url).replace(/^\/+/, "")}`;
};

const DashboardPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);

  const [range, setRange] = useState<RangeKey>("monthly");
  const [loading, setLoading] = useState(true);
  const [loadingCompletion, setLoadingCompletion] = useState(true);
  const [__, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({ open: false, message: "", severity: "success" });

  const [user, setUser] = useState<any>(null);
  const [projects, setProjects] = useState<ProjectAnalytics[]>([]);
  const [departmentLoad, setDepartmentLoad] = useState<Record<string, number>>(
    {}
  );
  const [recentComment, setRecentComment] = useState<string[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [events, setEvents] = useState<string[]>([]);
  const [completionStats, setCompletionStats] = useState<CompletionStats>({});
  const [statusFilter, setStatusFilter] = useState<
    Array<"TODO" | "IN_PROGRESS" | "DONE" | "POSTPONED">
  >(["TODO", "IN_PROGRESS", "DONE", "POSTPONED"]);

  // NEW: store summary counts as fallback
  const [summaryCounts, setSummaryCounts] = useState({
    todo: 0,
    inProgress: 0,
    done: 0,
    postponed: 0,
  });

  // Colors used across charts
  const statusColors = useMemo(
    () => ({
      DONE: theme.palette.success.main,
      IN_PROGRESS: theme.palette.warning.main,
      TODO: theme.palette.primary.main,
      POSTPONED: theme.palette.text.disabled,
    }),
    [theme]
  );

  const gridColor = alpha(theme.palette.text.primary, 0.12);
  const tickColor = theme.palette.text.primary;

  const fetchAll = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("accessToken");
      const userId = getUserInfoFromToken()?.id;

      // IMPORTANT: 7 requests -> 7 variables (keep order in sync)
      const [
        summaryRes,             // /dashboard/summary
        projectAnalyticsRes,    // /dashboard/project-analytics
        deptLoadRes,            // /dashboard/department-load
        recentCommentRes,       // /dashboard/recent-comment
        recentActivityRes,      // /dashboard/recent-activity
        eventsRes,              // /dashboard/upcoming-events
        assignmentsRes,         // /users/:id/assignments
      ] = await Promise.all([
        axios.get(`${baseUrl}/dashboard/summary`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${baseUrl}/dashboard/project-analytics`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${baseUrl}/dashboard/department-load`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${baseUrl}/dashboard/recent-comment`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${baseUrl}/dashboard/recent-activity`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${baseUrl}/dashboard/upcoming-events`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${baseUrl}/users/${userId}/assignments`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      // Normalize user object like Profile page
      const raw = assignmentsRes.data || {};
      const normalizedUser: any = {
        ...raw,
        profile: {
          name: "",
          title: "",
          age: "",
          gender: "",
          email: "",
          phone: "",
          address: "",
          notes: "",
          dateOfBirth: null,
          imageUrl: "",
          ...(raw.profile || {}),
        },
        positionsGroupedByDepartment: Array.isArray(raw.positionsGroupedByDepartment)
          ? raw.positionsGroupedByDepartment
          : [],
        projectsGroupedByDepartment: Array.isArray(raw.projectsGroupedByDepartment)
          ? raw.projectsGroupedByDepartment
          : [],
      };

      const possibleImg =
        raw?.profile?.imageUrl ??
        raw?.profile?.avatarUrl ??
        raw?.avatarUrl ??
        raw?.imageUrl ??
        "";
      normalizedUser.profile.imageUrl = normalizeImageUrl(possibleImg);

      // Set state from the CORRECT responses
      setUser(normalizedUser);
      setSummaryCounts({
        todo: Number(summaryRes?.data?.todo ?? 0),
        inProgress: Number(summaryRes?.data?.inProgress ?? 0),
        done: Number(summaryRes?.data?.done ?? 0),
        postponed: Number(summaryRes?.data?.postponed ?? 0),
      });
      setProjects(Array.isArray(projectAnalyticsRes.data) ? projectAnalyticsRes.data : []);
      setDepartmentLoad(deptLoadRes.data || {});
      setRecentComment(Array.isArray(recentCommentRes.data) ? recentCommentRes.data : []);
      setActivities(Array.isArray(recentActivityRes.data) ? recentActivityRes.data : []);
      setEvents(Array.isArray(eventsRes.data) ? eventsRes.data : []);
    } catch (e) {
      console.error(e);
      setError("Failed to load dashboard data.");
      setSnackbar({
        open: true,
        message: "Failed to load dashboard data.",
        severity: "error",
      });
      // reset to safe values
      setProjects([]);
      setDepartmentLoad({});
      setRecentComment([]);
      setActivities([]);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompletion = async (r: RangeKey) => {
    try {
      setLoadingCompletion(true);
      const token = localStorage.getItem("accessToken");
      const res = await axios.get(
        `${baseUrl}/dashboard/task-completion?range=${r}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCompletionStats(res.data || {});
    } catch (e) {
      console.error(e);
      setCompletionStats({});
      setSnackbar({
        open: true,
        message: "Failed to fetch task completion stats.",
        severity: "error",
      });
    } finally {
      setLoadingCompletion(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    fetchCompletion(range);
  }, [range]);

  // Totals computed from project analytics (fallback to summary if empty)
  const totalsFromProjects = useMemo(() => {
    if (!projects.length) {
      return { todo: 0, inProgress: 0, done: 0, postponed: 0 };
    }
    return projects.reduce(
      (acc, p) => {
        acc.todo += p.tasks?.todo || 0;
        acc.inProgress += p.tasks?.inProgress || 0;
        acc.done += p.tasks?.done || 0;
        acc.postponed += p.tasks?.postponed || 0;
        return acc;
      },
      { todo: 0, inProgress: 0, done: 0, postponed: 0 }
    );
  }, [projects]);

  const totals = useMemo(() => {
    // if project analytics is empty, display the /summary counts instead
    const hasProjectData =
      totalsFromProjects.todo +
        totalsFromProjects.inProgress +
        totalsFromProjects.done +
        totalsFromProjects.postponed >
      0;
    return hasProjectData ? totalsFromProjects : summaryCounts;
  }, [totalsFromProjects, summaryCounts]);

  const summaryTasks = useMemo(() => {
    return projects.reduce(
      (acc, p) => {
        acc.todo += p.tasks?.todo || 0;
        acc.inProgress += p.tasks?.inProgress || 0;
        acc.done += p.tasks?.done || 0;
        acc.postponed += p.tasks?.postponed || 0;
        acc.overdue += p.tasks?.overdue || 0;
        acc.urgent += p.tasks?.urgent || 0;
        acc.reminders += p.tasks?.reminders || 0;
        return acc;
      },
      {
        todo: totals.todo, // slight help if projects empty
        inProgress: totals.inProgress,
        done: totals.done,
        postponed: totals.postponed,
        overdue: 0,
        urgent: 0,
        reminders: 0,
      }
    );
  }, [projects, totals]);

  const completionRate = useMemo(() => {
    const total =
      totals.todo + totals.inProgress + totals.done + totals.postponed;
    return total ? Math.round((totals.done / total) * 100) : 0;
  }, [totals]);

  // Urgent/delayed snapshot
  const urgentProjects = useMemo(
    () =>
      projects
        .filter((p) => (p.tasks?.todo || 0) + (p.tasks?.postponed || 0) > 0)
        .slice(0, 6)
        .map((p) => ({
          project: p.name,
          todo: p.tasks?.todo || 0,
          postponed: p.tasks?.postponed || 0,
        })),
    [projects]
  );

  /** ------------------- Charts ------------------- */
  const donutData = useMemo(() => {
    const allowed = new Set(statusFilter);
    const values = [
      allowed.has("TODO") ? totals.todo : 0,
      allowed.has("IN_PROGRESS") ? totals.inProgress : 0,
      allowed.has("DONE") ? totals.done : 0,
      allowed.has("POSTPONED") ? totals.postponed : 0,
    ];
    return {
      labels: ["To Do", "In Progress", "Done", "Postponed"],
      datasets: [
        {
          data: values,
          backgroundColor: [
            statusColors.TODO,
            statusColors.IN_PROGRESS,
            statusColors.DONE,
            statusColors.POSTPONED,
          ],
          borderColor: theme.palette.background.paper,
          borderWidth: 2,
          hoverOffset: 6,
        },
      ],
    };
  }, [totals, statusColors, theme, statusFilter]);

  const lineChartData = useMemo(() => {
    const labels = Array.from(
      new Set(
        Object.values(completionStats)
          .flat()
          .map((s) => s.date)
      )
    ).sort();

    const datasets = Object.entries(completionStats).map(([status, stats]) => {
      const byDate = Object.fromEntries(stats.map((s) => [s.date, s.count]));
      const c =
        statusColors[status as keyof typeof statusColors] ||
        theme.palette.primary.main;
      return {
        label: status,
        data: labels.map((d) => byDate[d] || 0),
        borderColor: c,
        backgroundColor: alpha(c, 0.18),
        pointRadius: 2,
        fill: true,
        tension: 0.35,
      };
    });

    return { labels, datasets };
  }, [completionStats, statusColors, theme]);

  const baseOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "bottom" as const,
          labels: { color: tickColor },
        },
        tooltip: { intersect: false, mode: "index" as const },
      },
      scales: {
        x: {
          grid: { color: gridColor },
          ticks: { color: tickColor },
        },
        y: {
          grid: { color: gridColor },
          ticks: { color: tickColor },
        },
      },
    }),
    [gridColor, tickColor]
  );

  // Department load (horizontal)
  const deptLabels = useMemo(() => Object.keys(departmentLoad), [departmentLoad]);
  const deptData = useMemo(
    () => Object.values(departmentLoad),
    [departmentLoad]
  );
  const deptChartData = useMemo(
    () => ({
      labels: deptLabels,
      datasets: [
        {
          label: "Total Tasks",
          data: deptData,
          backgroundColor: alpha(theme.palette.primary.main, 0.85),
          borderRadius: 8,
          barThickness: 22,
        },
      ],
    }),
    [deptLabels, deptData, theme]
  );

  

 

  /** ------------------- UI ------------------- */
  const greeting = (() => {
    const name = user?.profile?.name ?? getUserInfoFromToken()?.username ?? "There";
    const hour = dayjs().hour();
    const slot =
      hour < 12 ? "Good Morning" : hour < 18 ? "Good Afternoon" : "Good Evening";
    return `${slot}, ${name}`;
  })();

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`
            : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
        minHeight: "100vh",
      }}
    >
      {/* Header */}
      <Fade in timeout={600}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems="center"
          justifyContent="space-between"
          gap={2}
          mb={4}
        >
          <Box>
            <Typography
              variant="h4"
              fontWeight={900}
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                mb: 0.5,
              }}
            >
              {greeting}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <EventRoundedIcon sx={{ fontSize: 18, opacity: 0.7 }} />
              {dayjs().format("dddd, MMMM D, YYYY")} • Keep things moving.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="contained"
              startIcon={<AddTaskRoundedIcon />}
              onClick={() => navigate("/new-task/0")}
              sx={{
                borderRadius: 3,
                textTransform: "none",
                fontWeight: 700,
                px: 3,
                py: 1.5,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.4)}`,
                },
              }}
            >
              New Task
            </Button>
            <Tooltip title="Refresh Dashboard">
              <IconButton
                onClick={fetchAll}
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "rotate(180deg)",
                    background: alpha(theme.palette.primary.main, 0.1),
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                <RefreshRoundedIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Fade>

      {/* KPI Row */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Fade in={!loading} timeout={800}>
            {loading ? (
              <Skeleton variant="rounded" height={140} sx={{ borderRadius: 3 }} />
            ) : (
              <KPICard
                onClick={() => navigate("/home")}
                sx={{
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(
                    theme.palette.primary.main,
                    0.05
                  )} 100%)`,
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2.5}>
                  <IconContainer
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.2),
                      color: theme.palette.primary.main,
                    }}
                  >
                    <AssignmentRoundedIcon sx={{ fontSize: 28 }} />
                  </IconContainer>
                  <Box flex={1}>
                    <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>
                      Total Tasks
                    </Typography>
                    <Typography variant="h4" fontWeight={900} sx={{ mt: 0.5 }}>
                      {numberFmt(
                        totals.todo + totals.inProgress + totals.done + totals.postponed
                      )}
                    </Typography>
                  </Box>
                </Stack>
              </KPICard>
            )}
          </Fade>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Fade in={!loading} timeout={1000}>
            {loading ? (
              <Skeleton variant="rounded" height={140} sx={{ borderRadius: 3 }} />
            ) : (
              <KPICard
                onClick={() => navigate("/home?status=DONE")}
                sx={{
                  background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.15)} 0%, ${alpha(
                    theme.palette.success.main,
                    0.05
                  )} 100%)`,
                  borderColor: alpha(theme.palette.success.main, 0.3),
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2.5}>
                  <IconContainer
                    sx={{
                      bgcolor: alpha(theme.palette.success.main, 0.2),
                      color: theme.palette.success.main,
                    }}
                  >
                    <TrendingUpRoundedIcon sx={{ fontSize: 28 }} />
                  </IconContainer>
                  <Box flex={1}>
                    <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>
                      Completion
                    </Typography>
                    <Typography variant="h4" fontWeight={900} sx={{ mt: 0.5 }}>
                      {`${completionRate}%`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                      Overall done / total
                    </Typography>
                  </Box>
                </Stack>
                <Box sx={{ position: "absolute", right: 12, bottom: 12, width: 100, height: 50 }}>
                  <Line
                    data={{
                      labels: Array.from({ length: 8 }).map((_, i) => `${i}`),
                      datasets: [
                        {
                          data:
                            completionStats.DONE?.slice(-8).map((p) => p.count) ??
                            Array.from({ length: 8 }, (_, i) => (i % 3) + 1),
                          borderColor: theme.palette.success.main,
                          backgroundColor: alpha(theme.palette.success.main, 0.25),
                          tension: 0.4,
                          fill: true,
                          pointRadius: 0,
                          borderWidth: 2,
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: { legend: { display: false }, tooltip: { enabled: false } },
                      scales: { x: { display: false }, y: { display: false } },
                    }}
                  />
                </Box>
              </KPICard>
            )}
          </Fade>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Fade in={!loading} timeout={1200}>
            {loading ? (
              <Skeleton variant="rounded" height={140} sx={{ borderRadius: 3 }} />
            ) : (
              <KPICard
                onClick={() =>
                  navigate(
                    `/home?status=TODO,IN_PROGRESS,POSTPONED,REVIEW&endDateTo=${new Date().toISOString()}`
                  )
                }
                sx={{
                  background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.15)} 0%, ${alpha(
                    theme.palette.error.main,
                    0.05
                  )} 100%)`,
                  borderColor: alpha(theme.palette.error.main, 0.3),
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2.5}>
                  <IconContainer
                    sx={{
                      bgcolor: alpha(theme.palette.error.main, 0.2),
                      color: theme.palette.error.main,
                    }}
                  >
                    <WarningAmberRoundedIcon sx={{ fontSize: 28 }} />
                  </IconContainer>
                  <Box flex={1}>
                    <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>
                      Overdue
                    </Typography>
                    <Typography variant="h4" fontWeight={900} sx={{ mt: 0.5 }}>
                      {numberFmt(summaryTasks.overdue)}
                    </Typography>
                  </Box>
                </Stack>
              </KPICard>
            )}
          </Fade>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Fade in={!loading} timeout={1400}>
            {loading ? (
              <Skeleton variant="rounded" height={140} sx={{ borderRadius: 3 }} />
            ) : (
              <KPICard
                onClick={() =>
                  navigate(
                    `/home?status=TODO,IN_PROGRESS,POSTPONED,REVIEW&importance=VERY_IMPORTANT`
                  )
                }
                sx={{
                  background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.15)} 0%, ${alpha(
                    theme.palette.warning.main,
                    0.05
                  )} 100%)`,
                  borderColor: alpha(theme.palette.warning.main, 0.3),
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2.5}>
                  <IconContainer
                    sx={{
                      bgcolor: alpha(theme.palette.warning.main, 0.2),
                      color: theme.palette.warning.main,
                    }}
                  >
                    <AccessTimeRoundedIcon sx={{ fontSize: 28 }} />
                  </IconContainer>
                  <Box flex={1}>
                    <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>
                      Urgent
                    </Typography>
                    <Typography variant="h4" fontWeight={900} sx={{ mt: 0.5 }}>
                      {numberFmt(summaryTasks.urgent)}
                    </Typography>
                  </Box>
                </Stack>
              </KPICard>
            )}
          </Fade>
        </Grid>
      </Grid>

      {/* Main triptych row */}
      <Stack direction={{ xs: "column", xl: "row" }} spacing={3} mb={4}>
        {/* Profile & Snapshot */}
        <Fade in timeout={800}>
          <SectionCard
            sx={{
              flex: "1 1 420px",
            }}
          >
          {loading ? (
            <Stack spacing={2}>
              <Skeleton variant="circular" width={84} height={84} />
              <Skeleton height={28} width={180} />
              <Skeleton height={18} width={120} />
              <Skeleton height={12} />
              <Skeleton height={12} width="80%" />
            </Stack>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Stack alignItems="center" spacing={1}>
                  <Avatar
                    src={user?.profile?.imageUrl}
                    sx={{
                      bgcolor: theme.palette.primary.main,
                      width: 84,
                      height: 84,
                      fontSize: 36,
                    }}
                  >
                    {user?.profile?.name?.[0] || "U"}
                  </Avatar>
                  <Chip
                    size="small"
                    label={`@${user?.username ?? "user"}`}
                    variant="outlined"
                    icon={<OpenInNewRoundedIcon fontSize="small" />}
                    onClick={() => navigate("/profile")}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={8}>
                <Typography fontWeight={900} fontSize={18}>
                  {user?.profile?.name ?? "—"}
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  {user?.profile?.title ? `“${user.profile.title}”` : "—"}
                </Typography>

                <Divider sx={{ my: 1.25 }} />

                <Typography fontWeight={700}>Departments</Typography>
                <Stack direction="row" spacing={0.5} mt={0.5} flexWrap="wrap">
                  {(user?.positionsGroupedByDepartment ?? []).map(
                    (g: { department: { name: string } }, i: number) => (
                      <Chip key={i} size="small" label={g.department.name} />
                    )
                  )}
                </Stack>

                <Typography fontWeight={700} mt={1}>
                  Positions
                </Typography>
                <Stack direction="row" spacing={0.5} mt={0.5} flexWrap="wrap">
                  {(user?.positionsGroupedByDepartment ?? [])
                    .slice(0, 3)
                    .flatMap(
                      (g: { positions: { name: string }[] }, gi: number) =>
                        g.positions.slice(0, 2).map((p, pi) => (
                          <Chip
                            key={`${gi}-${pi}`}
                            size="small"
                            color="secondary"
                            label={p.name}
                          />
                        ))
                    )}
                  {user?.positionsGroupedByDepartment?.some(
                    (g: { positions: any[] }) => g.positions.length > 2
                  ) && <Chip size="small" variant="outlined" label="+ more" />}
                </Stack>

                <Divider sx={{ my: 1.25 }} />

                <Typography fontWeight={700}>Projects</Typography>
                <Typography variant="body2" color="text.secondary">
                  📁{" "}
                  {user?.projectsGroupedByDepartment?.reduce(
                    (acc: number, g: { projects: any[] }) => acc + g.projects.length,
                    0
                  ) ?? 0}
                </Typography>
              </Grid>

              {/* Snapshot */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" fontWeight={800} gutterBottom>
                  📊 Task Snapshot
                </Typography>
                <Stack spacing={0.75}>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip
                      label={`📝 To Do: ${summaryTasks.todo}`}
                      size="small"
                      onDoubleClick={() => navigate("/home?status=TODO")}
                    />
                    <Chip
                      label={`🔄 In Progress: ${summaryTasks.inProgress}`}
                      size="small"
                      onDoubleClick={() => navigate("/home?status=IN_PROGRESS")}
                    />
                    <Chip
                      label={`⏸ Postponed: ${summaryTasks.postponed}`}
                      size="small"
                      onDoubleClick={() => navigate("/home?status=POSTPONED")}
                    />
                    <Chip
                      label={`✅ Done: ${summaryTasks.done}`}
                      size="small"
                      onDoubleClick={() => navigate("/home?status=DONE")}
                    />
                  </Stack>

                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip
                      label={`⚠️ Overdue: ${summaryTasks.overdue}`}
                      size="small"
                      color="error"
                      onDoubleClick={() =>
                        navigate(
                          `/home?status=TODO,IN_PROGRESS,POSTPONED,REVIEW&endDateTo=${new Date().toISOString()}`
                        )
                      }
                    />
                    <Chip
                      label={`🚨 Urgent: ${summaryTasks.urgent}`}
                      size="small"
                      color="warning"
                      onDoubleClick={() =>
                        navigate(
                          `/home?status=TODO,IN_PROGRESS,POSTPONED,REVIEW&importance=VERY_IMPORTANT`
                        )
                      }
                    />
                    <Chip
                      label={`🔔 Reminders: ${summaryTasks.reminders}`}
                      size="small"
                      color="info"
                    />
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
          )}
        </SectionCard>
      </Fade>

        {/* Center: Donut + Completion trend */}
        <Stack spacing={3} flex="1 1 520px">
          <Fade in timeout={1000}>
            <SectionCard>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
              <Typography variant="subtitle2" fontWeight={900}>
                Tasks Summary
              </Typography>
              <Stack direction="row" spacing={1}>
                <Chip
                  size="small"
                  label="TODO"
                  color={statusFilter.includes("TODO") ? "primary" : "default"}
                  onClick={() =>
                    setStatusFilter((s) =>
                      s.includes("TODO") ? s.filter((x) => x !== "TODO") : [...s, "TODO"]
                    )
                  }
                />
                <Chip
                  size="small"
                  label="IN_PROGRESS"
                  color={statusFilter.includes("IN_PROGRESS") ? "warning" : "default"}
                  onClick={() =>
                    setStatusFilter((s) =>
                      s.includes("IN_PROGRESS")
                        ? s.filter((x) => x !== "IN_PROGRESS")
                        : [...s, "IN_PROGRESS"]
                    )
                  }
                />
                <Chip
                  size="small"
                  label="DONE"
                  color={statusFilter.includes("DONE") ? "success" : "default"}
                  onClick={() =>
                    setStatusFilter((s) =>
                      s.includes("DONE") ? s.filter((x) => x !== "DONE") : [...s, "DONE"]
                    )
                  }
                />
                <Chip
                  size="small"
                  label="POSTPONED"
                  color={statusFilter.includes("POSTPONED") ? "default" : "default"}
                  onClick={() =>
                    setStatusFilter((s) =>
                      s.includes("POSTPONED")
                        ? s.filter((x) => x !== "POSTPONED")
                        : [...s, "POSTPONED"]
                    )
                  }
                />
              </Stack>
            </Stack>

            <Box sx={{ height: 220, position: "relative" }}>
              {loading ? (
                <Skeleton variant="rounded" height="100%" />
              ) : (
                <Doughnut
                  data={donutData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { position: "bottom", labels: { color: tickColor } },
                    },
                  }}
                />
              )}
            </Box>
          </SectionCard>
        </Fade>

        <Fade in timeout={1200}>
          <SectionCard>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <InsightsRoundedIcon fontSize="small" sx={{ opacity: 0.7 }} />
                <Typography variant="subtitle2" fontWeight={900}>
                  Task Completion
                </Typography>
              </Stack>

              <Stack direction="row" alignItems="center" spacing={1}>
                <FilterAltRoundedIcon sx={{ opacity: 0.6 }} />
                <ToggleButtonGroup
                  size="small"
                  color="primary"
                  exclusive
                  value={range}
                  onChange={(_, v: RangeKey | null) => v && setRange(v)}
                >
                  <ToggleButton value="weekly">Week</ToggleButton>
                  <ToggleButton value="monthly">Month</ToggleButton>
                  <ToggleButton value="yearly">Year</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            </Stack>

            <Box sx={{ height: 270 }}>
              {loadingCompletion ? (
                <Skeleton variant="rounded" height="100%" />
              ) : Object.keys(completionStats).length === 0 ? (
                <Box
                  sx={{
                    height: "100%",
                    display: "grid",
                    placeItems: "center",
                    color: "text.secondary",
                    border: `1px dashed ${alpha(theme.palette.text.primary, 0.2)}`,
                    borderRadius: 1,
                  }}
                >
                  No completion data for this range.
                </Box>
              ) : (
                <Line data={lineChartData} options={baseOptions} />
              )}
            </Box>
          </SectionCard>
        </Fade>
        </Stack>

        {/* Right: Events & Urgent */}
        <Fade in timeout={1400}>
          <SectionCard sx={{ flex: "1 1 360px" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography fontWeight={900}>
                <EventRoundedIcon fontSize="small" sx={{ mr: 0.5 }} />
                Upcoming Events
              </Typography>
              <Chip size="small" variant="outlined" label={`${events.length}`} sx={{ height: 22 }} />
            </Stack>

            {loading ? (
              <Stack spacing={1.25}>
                <Skeleton height={18} />
                <Skeleton height={18} />
                <Skeleton height={18} />
              </Stack>
            ) : (
              <List dense>
                {events.length === 0 ? (
                  <ListItem>
                    <ListItemText primary="No upcoming events." />
                  </ListItem>
                ) : (
                  events.map((e, i) => (
                    <ListItem key={i} disableGutters>
                      <ListItemText primary={`📅 ${e}`} />
                    </ListItem>
                  ))
                )}
              </List>
            )}

            <Divider sx={{ mt: 1.5, mb: 1 }} />

            {urgentProjects.length > 0 && (
              <>
                <Typography variant="subtitle2" fontWeight={900} mb={0.5}>
                  Urgent & Delayed
                </Typography>
                <Stack spacing={0.5}>
                  {urgentProjects.map((t, i) => (
                    <Typography key={i} fontSize={13}>
                      🚨 {t.project} — To Do: {t.todo}, Postponed: {t.postponed}
                    </Typography>
                  ))}
                </Stack>
              </>
            )}
          </SectionCard>
        </Fade>
      </Stack>

      {/* Tabs */}
      <Fade in timeout={1600}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            mt: 2,
            mb: 3,
            "& .MuiTabs-indicator": {
              height: 3,
              borderRadius: "3px 3px 0 0",
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            },
          }}
          variant="scrollable"
          allowScrollButtonsMobile
        >
          <StyledTab label="Department Load" />
          <StyledTab label="Project Analytics" />
          <StyledTab label="Recent" />
        </Tabs>
      </Fade>

      {/* Department Load */}
      {tab === 0 && (
        <Fade in={tab === 0} timeout={600}>
          <SectionCard>
          <Typography variant="h6" mb={2} fontWeight={900}>
            Department Task Load
          </Typography>
          <Box sx={{ height: 320 }}>
            {loading ? (
              <Skeleton variant="rounded" height="100%" />
            ) : deptLabels.length === 0 ? (
              <Box
                sx={{
                  height: "100%",
                  display: "grid",
                  placeItems: "center",
                  color: "text.secondary",
                  border: `1px dashed ${alpha(theme.palette.text.primary, 0.2)}`,
                  borderRadius: 1,
                }}
              >
                No department data.
              </Box>
            ) : (
              <Bar
                data={deptChartData}
                options={{
                  ...baseOptions,
                  indexAxis: "y" as const,
                  plugins: { ...baseOptions.plugins, legend: { display: false } },
                }}
              />
            )}
          </Box>
          </SectionCard>
        </Fade>
      )}

      {/* Project Analytics */}
      {tab === 1 && (
        <Fade in={tab === 1} timeout={600}>
          <SectionCard>
          {loading ? (
            <Stack spacing={1.5}>
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" height={68} />
              ))}
            </Stack>
          ) : projects.length === 0 ? (
            <Box
              sx={{
                height: 160,
                display: "grid",
                placeItems: "center",
                color: "text.secondary",
                border: `1px dashed ${alpha(theme.palette.text.primary, 0.2)}`,
                borderRadius: 1,
              }}
            >
              No projects.
            </Box>
          ) : (
            projects.map((project) => {
              const completed = project.tasks?.done || 0;
              const total =
                (project.tasks?.todo || 0) +
                (project.tasks?.inProgress || 0) +
                (project.tasks?.postponed || 0) +
                completed;
              const progress = total > 0 ? (completed / total) * 100 : 0;

              return (
                <ProjectCard
                  key={project.id}
                  onDoubleClick={() =>
                    navigate(
                      `/home?projectId=${project.id}&status=TODO,IN_PROGRESS,POSTPONED`
                    )
                  }
                  sx={{ mb: 2 }}
                >
                  <Typography fontWeight={800} fontSize={15} mb={0.25}>
                    📁 {project.name}
                  </Typography>

                  <Typography fontSize={13} color="text.secondary" mb={0.75}>
                    <span style={{ color: theme.palette.primary.main }}>
                      📝 To Do: {project.tasks?.todo ?? 0}
                    </span>{" "}
                    |{" "}
                    <span style={{ color: theme.palette.warning.main }}>
                      🔄 In Progress: {project.tasks?.inProgress ?? 0}
                    </span>{" "}
                    |{" "}
                    <span style={{ color: theme.palette.success.main }}>
                      ✅ Done: {project.tasks?.done ?? 0}
                    </span>{" "}
                    |{" "}
                    <span style={{ color: theme.palette.text.disabled }}>
                      ⏸ Postponed: {project.tasks?.postponed ?? 0}
                    </span>
                  </Typography>

                  {(project.tasks?.overdue ?? 0) > 0 && (
                    <Typography fontSize={13} color="error.main">
                      ⚠️ Overdue: {project.tasks?.overdue}
                    </Typography>
                  )}
                  {(project.tasks?.urgent ?? 0) > 0 && (
                    <Typography fontSize={13} color="warning.dark">
                      🚨 Urgent: {project.tasks?.urgent}
                    </Typography>
                  )}
                  {(project.tasks?.reminders ?? 0) > 0 && (
                    <Typography fontSize={13} color="info.main">
                      🔔 Reminders: {project.tasks?.reminders}
                    </Typography>
                  )}

                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    color={getProgressColor(progress)}
                    sx={{
                      height: 10,
                      borderRadius: 2,
                      mt: 1.5,
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 2,
                        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                      },
                    }}
                  />
                  <Typography fontSize={12} color="text.secondary" mt={0.75} fontWeight={600}>
                    Completion: {Math.round(progress)}%
                  </Typography>
                </ProjectCard>
              );
            })
          )}
          </SectionCard>
        </Fade>
      )}

      {/* Recent */}
      {tab === 2 && (
        <Fade in={tab === 2} timeout={600}>
          <SectionCard>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" fontWeight={900} gutterBottom>
                🔄 Recent Status Updates
              </Typography>
              {loading ? (
                <Stack spacing={1}>
                  <Skeleton height={20} />
                  <Skeleton height={20} />
                  <Skeleton height={20} />
                </Stack>
              ) : activities.length === 0 ? (
                <Typography color="text.secondary">No recent status changes.</Typography>
              ) : (
                <List dense>
                  {activities.map((a, i) => (
                    <ListItem key={i} disableGutters sx={{ alignItems: "flex-start" }}>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                            <strong>Task:</strong> <em>{a.taskTitle}</em>
                            <br />
                            <strong>Status:</strong> {a.newStatus}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {dayjs(a.date).fromNow()} • {new Date(a.date).toLocaleString()}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="h6" fontWeight={900} gutterBottom>
                💬 Recent Comments
              </Typography>
              {loading ? (
                <Stack spacing={1}>
                  <Skeleton height={20} />
                  <Skeleton height={20} />
                  <Skeleton height={20} />
                </Stack>
              ) : recentComment.length === 0 ? (
                <Typography color="text.secondary">No recent comments.</Typography>
              ) : (
                <List dense>
                  {recentComment.map((c, i) => (
                    <ListItem key={i} disableGutters>
                      <ListItemText primary={<Typography variant="body2">🗨️ {c}</Typography>} />
                    </ListItem>
                  ))}
                </List>
              )}
            </Grid>
          </Grid>
          </SectionCard>
        </Fade>
      )}

      {/* Error & notices */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DashboardPage;
